import { useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { LiveTimeoutsSetting } from "@mubin/contracts";
import { errorText, send } from "../lib/api";
import { ErrorNote, Field, useSubmit } from "./ui";

/** Live session timeouts (docs/06): technical settings for the whole organization, within safe ranges. */
export function LiveTimeoutsCard({
  organizationId,
  value,
}: {
  organizationId: string;
  value: LiveTimeoutsSetting;
}) {
  const qc = useQueryClient();
  const { busy, error, run } = useSubmit();
  const [accept, setAccept] = useState(String(value.acceptSeconds / 60));
  const [connect, setConnect] = useState(String(value.connectSeconds));
  const [reconnect, setReconnect] = useState(String(value.reconnectSeconds));
  const [saved, setSaved] = useState(false);
  const next = {
    acceptSeconds: Math.round(Number(accept) * 60),
    connectSeconds: Math.round(Number(connect)),
    reconnectSeconds: Math.round(Number(reconnect)),
  };
  const changed =
    next.acceptSeconds !== value.acceptSeconds ||
    next.connectSeconds !== value.connectSeconds ||
    next.reconnectSeconds !== value.reconnectSeconds;

  function save(e: FormEvent) {
    e.preventDefault();
    setSaved(false);
    void run(async () => {
      await send("PATCH", `/v1/organizations/${organizationId}/settings`, { liveTimeouts: next });
      await qc.invalidateQueries({ queryKey: ["organizations"] });
      setSaved(true);
    }, errorText);
  }

  return (
    <section className="card">
      <div className="card-h">
        <h2>مهل التسميع المباشر</h2>
      </div>
      <form className="form section" onSubmit={save}>
        <div className="form-row">
          <Field id="lt-accept" label="انتظار قبول المعلم (دقائق)" hint="من 1 إلى 120. بعدها يُلغى الطلب">
            <input
              id="lt-accept"
              inputMode="numeric"
              dir="ltr"
              value={accept}
              onChange={(e) => setAccept(e.target.value.replace(/\D/g, ""))}
            />
          </Field>
          <Field id="lt-connect" label="بدء الاتصال (ثوانٍ)" hint="من 15 إلى 300">
            <input
              id="lt-connect"
              inputMode="numeric"
              dir="ltr"
              value={connect}
              onChange={(e) => setConnect(e.target.value.replace(/\D/g, ""))}
            />
          </Field>
          <Field
            id="lt-reconnect"
            label="مهلة إعادة الاتصال (ثوانٍ)"
            hint="من 30 إلى 600. بعدها تُعدّ الجلسة منقطعة"
          >
            <input
              id="lt-reconnect"
              inputMode="numeric"
              dir="ltr"
              value={reconnect}
              onChange={(e) => setReconnect(e.target.value.replace(/\D/g, ""))}
            />
          </Field>
        </div>
        <ErrorNote error={error} />
        {saved && !changed && <p className="muted small">حُفظت، وتسري على الطلبات الجديدة.</p>}
        <div className="form-actions">
          <button className="btn btn-primary" disabled={busy || !changed}>
            {busy ? "جارٍ الحفظ…" : "حفظ المهل"}
          </button>
        </div>
      </form>
    </section>
  );
}
